import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { Navigate } from '@sorare/routing';

import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { ANY_SPORT_SCOUTING_CENTER_WATCHLISTS } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import {
  sportAnalyticTag,
  useSportContext,
} from '@sorare/core/src/contexts/sport';
import { scoutingCenter } from '@sorare/core/src/lib/glossary';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';

import { WatchlistEmptyState } from 'components/watchlists/WatchlistEmptyState';
import { WatchlistHeader } from 'components/watchlists/WatchlistHeader';
import { FILTERS_BY_SPORT, SORTS_BY_SPORT } from 'lib/scoutingCenterFilters';
import { AdvancedWatchlistSearch } from 'searchCommonPlayer/AdvancedPlayerSearch/AdvancedWatchlistSearch';

import type { WatchlistPage_watchlist } from './__generated__/index.graphql';

type Props = {
  watchlist: Nullable<WatchlistPage_watchlist>;
  so5FixtureSlug?: string;
};
export const WatchlistPage = ({ watchlist, so5FixtureSlug }: Props) => {
  const { isMe } = useCurrentUserContext();
  const { sport: sportContext } = useSportContext();
  if (!watchlist)
    return (
      <Navigate
        to={generateSportPath(ANY_SPORT_SCOUTING_CENTER_WATCHLISTS, {
          sport: sportContext,
        })}
      />
    );

  const { sport } = watchlist;

  const SORTS = SORTS_BY_SPORT[sport];
  const FILTERS = FILTERS_BY_SPORT[sport];
  return (
    <>
      <PageHeader text={scoutingCenter.watchlists} />
      <WatchlistHeader watchlist={watchlist} />
      {watchlist.totalPlayersCount === 0 ? (
        <WatchlistEmptyState />
      ) : (
        <AdvancedWatchlistSearch
          sport={sport}
          key={watchlist.id}
          sorts={SORTS}
          defaultSort={SORTS[0]}
          cardFilters={FILTERS}
          analyticsTags={['Watchlist', sportAnalyticTag[sport]]}
          topic={{ type: 'watchlist', label: '' }}
          attributesToRetrieve={['slug']}
          watchlistId={watchlist.id}
          so5FixtureSlug={so5FixtureSlug}
          canManageWatchlist={isMe(watchlist.user)}
        />
      )}
    </>
  );
};

WatchlistPage.fragments = {
  watchlist: gql`
    fragment WatchlistPage_watchlist on Watchlist {
      slug
      id
      sport
      user {
        slug
      }
      totalPlayersCount
      ...WatchlistHeader_watchlist
    }
    ${WatchlistHeader.fragments.watchlist}
  ` as TypedDocumentNode<WatchlistPage_watchlist>,
};
